/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the patient summary component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * React
 */
import * as React from 'react';

import { useHistory } from 'react-router-dom';

import { makeStyles, Theme } from '@material-ui/core/styles';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';

/**
 * Used to format text.
 */
import Typography from '@material-ui/core/Typography';

/**
 * Used to style expanding areas.
 */
import ExpansionPanel from '@material-ui/core/ExpansionPanel';

/**
 * Used to style expanding areas.
 */
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';

/**
 * Used to style expanding areas.
 */
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';

import { common } from '@material-ui/core/colors';

import * as Dtos from '../../api/Neon.Dtos';

import { RequestState } from '@ngt/request-utilities';

import Chip from '@material-ui/core/Chip';

import { Column, Row } from 'react-table';

import CircularProgress from '@material-ui/core/CircularProgress';

import { ALL_COUNTRIES_CODE, ALL_INSTITUTIONS_CODE, ALL_TREATMENTS_CODE, DataTable, IPatient, onlinePatientManagement, usePatients } from '@ngt/online-patient-management-ui';
import { DateTime } from 'luxon';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */




/*
 * ---------------------------------------------------------------------------------
 * interfaces
 * ---------------------------------------------------------------------------------
 */

interface IPatientSummaryTableProps {
    title: string;
    countryCode?: string | null;
    institutionCode?: string | null;
    treatmentId?: number | null;
    patientStatuses?: Array<number> | null;
    columns: Array<Column<IPatient>>;
    path?: string | null;
    dateAfter?: DateTime | null; //date to filter patients after this date
}
/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    expansionPanelContainer: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    header: {
        background: theme.palette.primary.main,
        color: common.white,

        '&.Mui-expanded': {
            minHeight: 48
        },

        '& .MuiExpansionPanelSummary-content': {
            justifyContent: 'space-between',

            '&.Mui-expanded': {
                marginTop: theme.spacing(1.5),
                marginBottom: theme.spacing(1.5)
            }
        }
    },
    expand: {
        color: common.white
    },
    expandContent: {
        padding: theme.spacing(0)
    },
    tableWrapper: {
        overflow: 'auto',
        width: '100%'
    },
    chip: {
        backgroundColor: '#987ba7',
        color: theme.palette.common.white,
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */
export const DEFAULT_PATIENT_SUMMARY_ROUTE = '/registration/:countryCode/:institutionCode/:patientStudyNumber';

const PatientSummaryTable: React.FunctionComponent<IPatientSummaryTableProps> = ({
    title,
    countryCode,
    institutionCode,
    treatmentId,
    patientStatuses,
    columns,
    path,
    dateAfter
}) => {
    const classes = useStyles();

    const history = useHistory();

    const institutionCodeToUse = React.useMemo(() => {
        return !institutionCode || institutionCode === ALL_INSTITUTIONS_CODE ? null : institutionCode;
        
    }, [institutionCode]);

    const countryCodeToUse = React.useMemo(() => {
        return !countryCode || countryCode === ALL_COUNTRIES_CODE ? null : countryCode; 
    }, [countryCode]);

    const treatmentIdToUse = React.useMemo(() => {
        
        if(treatmentId === 3 && patientStatuses && Array.isArray(patientStatuses) && (patientStatuses.includes(Dtos.PatientStatuses.Screened) || patientStatuses.includes(Dtos.PatientStatuses.Ineligible))){
            return null; // Special case for treatment 3 (Arm C) When filtering for treatment 3, we need to include patients with treatment 3 and status Screened or Ineligible
        }
        else{    
            return !treatmentId || treatmentId === ALL_TREATMENTS_CODE ? null : treatmentId; 
        }
    }, [treatmentId]);

    const pathToUse = React.useMemo(() => {
        return !path ? DEFAULT_PATIENT_SUMMARY_ROUTE : path;
    }, [path]);

    const [expanded, setExpanded] = React.useState(true);

    const [patients, patientsLoadState, patientsActions] = usePatients(
        undefined,
        institutionCodeToUse ? undefined : countryCodeToUse ?? undefined,
        undefined,
        institutionCodeToUse ?? undefined,
        treatmentIdToUse ?? undefined
    );
    const patientsToUse = React.useMemo(() => {
        if (!Array.isArray(patientStatuses)) return [];

        const hasStatus = (statuses: number[]) =>
            statuses.some(status => patientStatuses.includes(status));

        // Special handling for treatment 3 (Arm C)
        if (treatmentId === 3) {
            // Registered or Randomised
            if (hasStatus([Dtos.PatientStatuses.Registered, Dtos.PatientStatuses.Randomised])) {
                return patients?.filter(p => (patientStatuses ?? []).find(x => x === p.status) != null) ?? [];
            }
            // Ineligible or Screened
            if (hasStatus([Dtos.PatientStatuses.Ineligible, Dtos.PatientStatuses.Screened])) {
                let filtered = patients?.filter(p => (patientStatuses ?? []).find(x => x === p.status) != null) ?? [];
                if (dateAfter?.isValid) {
                    filtered = filtered.filter(p => {
                        const entered = DateTime.fromISO(p.enteredDate ?? "");
                        return entered.isValid && entered >= dateAfter;
                    });
                }
                return filtered;
            }
        }
        // Default filter
        return patients?.filter(p => (patientStatuses ?? []).find(x => x === p.status) != null) ?? [];
    }, [patients, treatmentId, patientStatuses, dateAfter]);


    
    React.useEffect(() => {
        patientsActions.load();

        return () => {
            patientsActions.clear();
        }
    }, [patientsActions, patientStatuses]);
    
    const loading = patientsLoadState.state === RequestState.None || patientsLoadState.state === RequestState.Pending;

    const disabled = !loading && (!patientsToUse || patientsToUse.length === 0);

    const onRowClick = React.useCallback((row: Row<IPatient>) => {
        const patientSummaryUrl = pathToUse
            .replace(`:${onlinePatientManagement.routeParameters.countryCode}`, countryCodeToUse ?? ALL_COUNTRIES_CODE)
            .replace(`:${onlinePatientManagement.routeParameters.institutionCode}`, institutionCodeToUse ?? ALL_INSTITUTIONS_CODE)
            .replace(`:${onlinePatientManagement.routeParameters.patientStudyNumber}`, row.values.studyNumber)

        history.push(patientSummaryUrl);
    }, [pathToUse, countryCodeToUse, institutionCodeToUse, treatmentIdToUse])

    const onClick = React.useCallback(() => {
        setExpanded(!expanded)
    }, [expanded, setExpanded])

    return (
        <ExpansionPanel
            disabled={disabled}
            expanded={expanded && !disabled && !loading}
            className={classes.expansionPanelContainer}
        >
            <ExpansionPanelSummary
                onClick={onClick}
                expandIcon={loading ? <CircularProgress color="secondary" size={16} /> : disabled ? false : <FontAwesomeIcon className={classes.expand} icon={faChevronDown} fixedWidth />}
                aria-controls={`${title}-content`}
                id="`${title}-header`"
                className={classes.header}
            >
                <Typography>
                    {title} Patients
                </Typography>
                {
                    disabled && (
                        <Typography>
                            There are no {title.toLowerCase()} patients
                        </Typography>
                    )
                }
                {
                    !loading && patientsToUse && !disabled && (
                        <Chip
                            label={`${patientsToUse.length} Patient${(patientsToUse.length > 1 ? 's' : '')}`}
                            className={classes.chip}
                            size="small"
                        />
                    )
                }
            </ExpansionPanelSummary>
            <ExpansionPanelDetails
                className={classes.expandContent}
            >
                <div className={classes.tableWrapper}>
                {Array.isArray(patientsToUse) && patientsToUse.length > 0 && !loading ? ( //When a treatment is switched, usePatients becomes empty when it clears and reloads the data, without this it would cause RangeError: Invalid array length
                    <DataTable
                    data={patientsToUse}
                    columns={columns}
                    onRowClick={onRowClick}
                    />
                ) : null}
                </div>

            </ExpansionPanelDetails>
        </ExpansionPanel>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default PatientSummaryTable;
